"use client";

import { Timeline } from "../HorizontalTimeline/Timeline";

const ZeroDaysContent = ({ blok }) => {
  // Function to apply styles dynamically based on the marks
  const applyStyles = (marks) => {
    let styles = {};

    // Loop through all marks and apply corresponding styles
    marks.forEach((mark) => {
      switch (mark.type) {
        case "bold":
          styles.fontWeight = "bold";
          break;
        case "italic":
          styles.fontStyle = "italic";
          break;
        case "underline":
          styles.textDecoration = "underline";
          break;
        case "strikethrough":
          styles.textDecoration = "line-through";
          break;
        case "textStyle":
          if (mark.attrs && mark.attrs.color) {
            styles.color = mark.attrs.color;
          }
          break;
        case "superscript":
          styles.verticalAlign = "super";
          styles.fontSize = "smaller"; // Optional: smaller text for superscript
          break;
        case "subscript":
          styles.verticalAlign = "sub";
          styles.fontSize = "smaller"; // Optional: smaller text for subscript
          break;
        // Add more cases for other marks you might need in the future
        default:
          break;
      }
    });

    return styles;
  };

  const renderRichText = (content) => {
    return content?.map((blok, index) => {

      switch (blok.type) {
        case "paragraph":
          return (
            <div key={index} className="mt-2 mb-5">
              {blok?.content?.map((textBlock, textIndex) => {
                const styles = textBlock?.marks ? applyStyles(textBlock.marks) : {};

                // Handle images
                if (textBlock?.type === "image") {
                  return (
                    <figure key={textIndex} className="my-5">
                      <img
                        src={textBlock?.attrs?.src}
                        alt={textBlock?.attrs?.alt || ""}
                        className="w-full max-w-[100%] h-auto rounded-lg shadow-md object-cover"
                      />
                      {textBlock?.attrs?.caption && (
                        <figcaption className="mt-2 text-sm text-gray-600">
                          {textBlock?.attrs?.caption}
                        </figcaption>
                      )}
                    </figure>
                  );
                }

                // Handle links
                if (textBlock?.marks?.some((mark) => mark.type === "link")) {
                  const linkMark = textBlock?.marks?.find((mark) => mark.type === "link");
                  return (
                    <a
                      key={textIndex}
                      href={linkMark?.attrs?.href}
                      target={linkMark?.attrs?.target || "_blank"}
                      style={styles}
                      rel="noopener noreferrer"
                    >
                      {textBlock.text}
                    </a>
                  );
                }

                return (
                  <span key={textIndex} style={styles}>
                    {textBlock.text}
                  </span>
                );
              })}
            </div>
          );

        case "heading": {
          const headingSizes = {
            1: "36px",
            2: "32px",
            3: "36px",
            4: "28px",
            5: "18px",
            6: "24px",
          };
          const fontSize = headingSizes[blok.attrs?.level] || "36px";

          return (
            <div
              key={index}
              style={{
                fontSize,
                color: "#5D2AE7",
                fontWeight: 700,
                lineHeight: "55px",
              }}
              className="bg-custom-radial bg-clip-text text-transparent"
            >
              {blok?.content?.map((textBlock, textIndex) => {
                const styles = textBlock.marks ? applyStyles(textBlock.marks) : {};

                // Handle links
                if (textBlock.marks?.some((mark) => mark.type === "link")) {
                  const linkMark = textBlock.marks.find((mark) => mark.type === "link");
                  return (
                    <a
                      key={textIndex}
                      href={linkMark?.attrs?.href}
                      target={linkMark?.attrs?.target || "_self"}
                      style={styles}
                      rel="noopener noreferrer"
                    >
                      {textBlock.text}
                    </a>
                  );
                }

                return (
                  <span key={textIndex} style={styles}>
                    {textBlock.text}
                  </span>
                );
              })}
            </div>
          );
        }

        case "ordered_list":
        case "unordered_list":
        case "bullet_list":
          return (
            <ol key={index} className="list-decimal">
              {blok?.content?.map((listItem, listIndex) => (
                <li key={listIndex} className="my-5 ml-4">
                  {listItem?.content.map((paragraph, paragraphIndex) => (
                    <span key={paragraphIndex} style={{ marginBottom: "12px" }}>
                      {paragraph?.content?.map((textBlock, textIndex) => {
                        if (textBlock.marks?.some((mark) => mark.type === "link")) {
                          const linkMark = textBlock.marks.find((mark) => mark.type === "link");
                          return (
                            <a
                              key={textIndex}
                              href={linkMark?.attrs?.href}
                              target={linkMark?.attrs?.target || "_self"}
                              // style={styles}
                              className="text-wrap text-blue-500 underline italic"
                              rel="noopener noreferrer"
                            >
                              {textBlock.text}
                            </a>
                          );
                        }
                        return <span
                          key={textIndex}
                          style={{
                            fontWeight:
                              paragraph.content.length > 1 && textIndex === 0 ? "normal" : "normal",
                          }}
                        >
                          {textBlock.text}
                        </span>
                      })}
                    </span>
                  ))}
                </li>
              ))}
            </ol>
          );

        case "table":
          return (
            <div key={index} className="overflow-x-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse', margin: '20px 0' }}>
                <thead>
                  {blok?.content?.find(row => row.type === "tableRow" && row.content.some(cell => cell.type === "tableHeader")) && (
                    <tr>
                      {blok?.content.find(row => row.type === "tableRow").content.map((header, headerIndex) => (
                        header.type === "tableHeader" && (
                          <th key={headerIndex} style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#f2f2f2', textAlign: 'left' }}>
                            {renderRichText(header.content)}
                          </th>
                        )
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {blok?.content?.filter(row => row.type === "tableRow" && row.content.some(cell => cell.type === "tableCell")).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row?.content?.map((cell, cellIndex) => (
                        cell.type === "tableCell" && (
                          <td key={cellIndex} style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {renderRichText(cell.content)}
                          </td>
                        )
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        default:
          return null;
      }
    });
  };

  return (
    <div style={{
      backgroundSize: "cover", // Ensure the image covers the entire section
      backgroundRepeat: "repeat", // Prevent repeating the background
    }}
      className="max-w-7xl w-full text-center lg:text-left px-4"
    >
      <div className='pb-20 flex flex-col items-center w-full'>
        <div className="prose max-w-none  text-left">
          <div>{renderRichText(blok?.content?.content)}</div>
        </div>
      </div>
    </div>
  );
};

export default ZeroDaysContent;
